import type { ReactNode } from 'react';
import { Calendar, Mail, MessagesSquare, Plus, UserPlus } from 'lucide-react';
import type { CandidateDetailDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import { CreateInterviewDialog } from '../../../interviews/containers/CreateInterviewDialog';
import { CandidateInterviewsContainer } from '../CandidateInterviewsContainer';
import { formatDate } from '../../../../utils/formatDate';

interface CandidateDetailContainerContentProps {
  orgId: string;
  candidate: CandidateDetailDto;
}

export function CandidateDetailContainerContent({
  orgId,
  candidate,
}: CandidateDetailContainerContentProps) {
  const dialog = useDialog(CreateInterviewDialog);
  const initials =
    `${candidate.firstName.charAt(0)}${candidate.lastName.charAt(0)}`.toUpperCase() || '··';
  const fullName = `${candidate.firstName} ${candidate.lastName}`.trim() || 'Candidate';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[13px] font-semibold text-primary ring-1 ring-primary/20"
            aria-hidden
          >
            {initials}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-[18px] font-semibold leading-tight text-foreground">
              {fullName}
            </h1>
            <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
              <Mail className="h-3 w-3" strokeWidth={1.6} />
              <span className="truncate">{candidate.email}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="tinted"
            shape="pill"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              dialog.open({
                orgId,
                lockedCandidate: {
                  id: candidate.id,
                  firstName: candidate.firstName,
                  lastName: candidate.lastName,
                  email: candidate.email,
                },
              })
            }
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.6} />
            Invite to interview
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <DetailTile
          icon={<UserPlus className="h-3.5 w-3.5" strokeWidth={1.6} />}
          label="Created"
          value={
            <span className="text-[14px] font-medium text-foreground">
              {formatDate(candidate.createdAt)}
            </span>
          }
          sub="Candidate added"
        />
        <DetailTile
          icon={<MessagesSquare className="h-3.5 w-3.5" strokeWidth={1.6} />}
          label="Total interviews"
          value={
            <span className="font-mono text-[20px] font-semibold tabular-nums text-foreground">
              {candidate.interviewCount}
            </span>
          }
          sub={
            candidate.interviewCount === 1
              ? '1 invite sent'
              : `${candidate.interviewCount} invites sent`
          }
        />
        <DetailTile
          icon={<Calendar className="h-3.5 w-3.5" strokeWidth={1.6} />}
          label="Last interview"
          value={
            <span className="text-[14px] font-medium text-foreground">
              {formatDate(candidate.lastInterviewAt, null) ?? '—'}
            </span>
          }
          sub={candidate.lastInterviewAt ? 'Most recent invite' : 'No interviews yet'}
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3 border-b border-border">
          <h2 className="relative inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium text-foreground">
            <MessagesSquare className="h-3.5 w-3.5 text-primary" strokeWidth={1.6} />
            Interviews
            <span aria-hidden className="absolute -bottom-px left-0 right-0 h-px bg-foreground" />
          </h2>
          {candidate.interviewCount > 0 && (
            <span className="pb-2 text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground/80">
              {candidate.interviewCount} total
            </span>
          )}
        </div>
        <CandidateInterviewsContainer orgId={orgId} candidateId={candidate.id} />
      </div>
    </div>
  );
}

function DetailTile({
  icon,
  label,
  value,
  sub,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3.5 transition-shadow hover:shadow-[0_1px_0_rgb(0_0_0/0.04),0_4px_12px_-4px_rgb(0_0_0/0.06)]">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-secondary text-muted-foreground">
          {icon}
        </span>
        <span className="text-[10.5px] font-medium uppercase tracking-[0.1em]">{label}</span>
      </div>
      <div className="mt-2.5 min-h-[28px]">{value}</div>
      <div className="mt-1.5 text-[11.5px] text-muted-foreground/80">{sub}</div>
    </div>
  );
}
